import { X } from 'lucide-react';
import MemberSearchInput from './MemberSearchInput';
import styles from './PlayerRow.module.css';

/**
 * Shared editable player row.
 *
 * Props:
 *   player       — player state object (type, memberNumber, memberName,
 *                  guestName, needsCart, holes, status)
 *   idx          — row index (0-based)
 *   onChange     — fn(idx, patch) — merges patch into player at idx
 *   onMemberSelect — fn(idx, member | null) — called when a member is
 *                    picked from the search dropdown
 *   onRemove     — fn(idx)
 *   canRemove    — when false the remove button is disabled
 *   disabled     — disables all interactive controls
 *   showStatus   — when false the Booked/Paid/No Show toggle is hidden
 *                  (default true)
 *   courseHoles  — when 9 the 18-holes option is disabled (default 18)
 */
export default function PlayerRow({
  player,
  idx,
  onChange,
  onMemberSelect,
  onRemove,
  canRemove,
  disabled = false,
  showStatus = true,
  courseHoles = 18,
}) {
  return (
    <div className={styles.playerRow}>
      {/* Top row */}
      <div className={styles.playerRowTop}>
        {/* Member / Guest type toggle */}
        <div className={styles.typeToggle} role="group" aria-label="Player type">
          <button
            type="button"
            className={`${styles.typeBtn} ${player.type === 'member' ? styles.typeBtnActive : ''}`}
            onClick={() => onChange(idx, { type: 'member', guestName: '' })}
            disabled={disabled}
          >
            Member
          </button>
          <button
            type="button"
            className={`${styles.typeBtn} ${player.type === 'guest' ? styles.typeBtnActive : ''}`}
            onClick={() => onChange(idx, { type: 'guest', memberNumber: '', memberName: '' })}
            disabled={disabled}
          >
            Guest
          </button>
        </div>

        {/* Name field */}
        {player.type === 'member' ? (
          <MemberSearchInput
            value={player.memberName}
            onSelect={(member) => onMemberSelect(idx, member)}
            disabled={disabled}
          />
        ) : (
          <div className={styles.playerInputWrap}>
            <input
              type="text"
              className={styles.playerInput}
              value={player.guestName}
              onChange={(e) => onChange(idx, { guestName: e.target.value })}
              placeholder="Guest name"
              disabled={disabled}
              aria-label="Guest name"
            />
          </div>
        )}

        {/* Remove */}
        <button
          type="button"
          className={styles.removeBtn}
          onClick={() => onRemove(idx)}
          disabled={!canRemove || disabled}
          aria-label="Remove player"
          title="Remove player"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>

      {/* Bottom row */}
      <div className={styles.playerRowBottom}>
        {/* Needs cart */}
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={player.needsCart}
            onChange={(e) => onChange(idx, { needsCart: e.target.checked })}
            disabled={disabled}
          />
          Needs cart
        </label>

        {/* Holes */}
        <div className={styles.holesWrap}>
          <span className={styles.holesLabel}>Holes:</span>
          <select
            className={styles.holesSelect}
            value={player.holes ?? ''}
            onChange={(e) =>
              onChange(idx, { holes: e.target.value === '' ? null : parseInt(e.target.value, 10) })
            }
            disabled={disabled}
            aria-label="Holes to play"
          >
            <option value="" disabled>Select holes…</option>
            <option value={9}>9</option>
            <option value={18} disabled={courseHoles === 9}>18</option>
          </select>
        </div>

        {/* Status toggle (optional) */}
        {showStatus && (
          <div className={styles.typeToggle} role="group" aria-label="Player status">
            <button
              type="button"
              className={`${styles.typeBtn} ${player.status === 'Booked' || !player.status ? styles.typeBtnActive : ''}`}
              onClick={() => onChange(idx, { status: 'Booked' })}
              disabled={disabled}
            >
              Booked
            </button>
            <button
              type="button"
              className={`${styles.typeBtn} ${player.status === 'Paid' ? styles.typeBtnPaidActive : ''}`}
              onClick={() => onChange(idx, { status: 'Paid' })}
              disabled={disabled}
            >
              Paid
            </button>
            <button
              type="button"
              className={`${styles.typeBtn} ${player.status === 'No Show' ? styles.typeBtnNoShowActive : ''}`}
              onClick={() => onChange(idx, { status: 'No Show' })}
              disabled={disabled}
            >
              No show
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
